/**
 * BottomNav 컴포넌트
 *
 * 화면 하단에 고정되는 탭 네비게이션
 * - 5개 탭: 홈, 현황, 등록, 통계, 정산
 * - React Router의 NavLink 사용
 * - Material UI 아이콘 사용 (active/inactive 상태 지원)
 */

import { NavLink } from 'react-router-dom'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import HomeIcon from '@mui/icons-material/Home'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import GroupIcon from '@mui/icons-material/Group'

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<SvgIconProps>
  activeIcon: React.ComponentType<SvgIconProps>
  isMain?: boolean
}

const navItems: NavItem[] = [
  {
    path: '/',
    icon: HomeOutlinedIcon,
    activeIcon: HomeIcon,
    label: '홈',
  },
  {
    path: '/status',
    icon: AccountBalanceWalletOutlinedIcon,
    activeIcon: AccountBalanceWalletIcon,
    label: '현황',
  },
  {
    path: '/register',
    icon: AddCircleOutlineIcon,
    activeIcon: AddCircleOutlineIcon,
    label: '등록',
    isMain: true,
  },
  {
    path: '/statistics',
    icon: BarChartOutlinedIcon,
    activeIcon: BarChartIcon,
    label: '통계',
  },
  {
    path: '/settlement',
    icon: GroupOutlinedIcon,
    activeIcon: GroupIcon,
    label: '정산',
  },
]

export function BottomNav() {
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-50"
      aria-label="메인 네비게이션"
    >
      <div className="max-w-4xl mx-auto flex">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              item.isMain
                ? 'flex-1 flex flex-col items-center justify-center gap-1 min-h-16 px-2'  // 등록: 색상 조건 제거
                : `flex-1 flex flex-col items-center justify-center gap-1 min-h-16 px-2 transition-colors ${
                    isActive
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`
            }
            aria-label={item.label}
          >
            {({ isActive }) => {
              const IconComponent = isActive ? item.activeIcon : item.icon
              return (
                <>
                  {/* 등록 탭: 원형 그라디언트 배경 */}
                  {item.isMain ? (
                    <div className="rounded-full p-2 bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-400 dark:to-primary-600 shadow-lg">
                      <IconComponent
                        className="text-white"
                        sx={{ fontSize: 28 }}
                        aria-hidden="true"
                      />
                    </div>
                  ) : (
                    /* 다른 탭: 기존 스타일 유지 */
                    <IconComponent
                      sx={{ fontSize: 28 }}
                      aria-hidden="true"
                    />
                  )}

                  {/* 라벨: 등록은 항상 primary 색상 */}
                  <span className={`text-xs ${
                    item.isMain
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : ''
                  }`}>
                    {item.label}
                  </span>
                </>
              )
            }}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}
